/**
 * Shared search utilities for consistent search behaviour across the application
 */

/**
 * Extract a UK postcode from a string (e.g., an address)
 * @param {string} text - The text to search for a postcode
 * @returns {string|null} - The extracted postcode or null if not found
 */
export const extractPostcode = (text) => {
  if (!text) return null;

  // Try to find a UK postcode pattern in the text
  const match = text.match(/[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}/i);
  return match ? match[0].toUpperCase() : null;
};

/**
 * Check if a string looks like a postcode search (full or partial)
 * @param {string} term - The search term to check
 * @returns {boolean} - True if the term looks like a postcode search
 */
export const looksLikePostcode = (term) => {
  if (!term) return false;
  const cleaned = term.replace(/\s/g, '').toUpperCase();
  // Matches partial postcodes like "BB5", "M1", "SW1A" or full postcodes
  return /^[A-Z]{1,2}[0-9][A-Z0-9]?/.test(cleaned);
};

/**
 * Match postcodes with flexible matching for partial inputs
 * Supports partial postcode searches (e.g., "BB5" matches "BB5 1AB")
 * @param {string} postcode - The postcode to check against
 * @param {string} searchTerm - The search term (can be partial)
 * @returns {boolean} - True if the postcode matches the search term
 */
export const matchPostcode = (postcode, searchTerm) => {
  if (!postcode || !searchTerm) return false;

  // Clean up inputs for comparison (remove spaces, uppercase)
  const cleanPostcode = postcode.replace(/\s/g, '').toUpperCase();
  const cleanSearchTerm = searchTerm.replace(/\s/g, '').toUpperCase();

  // Match beginning of postcode for partial searches (e.g., "BB5" matches "BB51AB")
  // Also allow matching anywhere in the postcode for flexibility
  return cleanPostcode.startsWith(cleanSearchTerm) || cleanPostcode.includes(cleanSearchTerm);
};

/**
 * Check if a job/marker matches a postcode search term
 * Checks direct postcode field first, then falls back to extracting from address
 * @param {object} item - The job or marker object
 * @param {string} searchTerm - The search term
 * @returns {boolean} - True if the item's postcode matches
 */
export const matchItemPostcode = (item, searchTerm) => {
  if (!item || !searchTerm) return false;

  // Check direct postcode field first (preferred - from Android app and web app)
  if (item.postcode && matchPostcode(item.postcode, searchTerm)) {
    return true;
  }

  // Fallback: Extract postcode from address strings (for legacy records without postcode field)
  const extractedFromStreet = extractPostcode(item.streetAddress);
  if (extractedFromStreet && matchPostcode(extractedFromStreet, searchTerm)) {
    return true;
  }

  const extractedFromFormatted = extractPostcode(item.formattedAddress);
  if (extractedFromFormatted && matchPostcode(extractedFromFormatted, searchTerm)) {
    return true;
  }

  return false;
};

/**
 * Check if a job/marker matches a search term across all searchable fields
 * Uses smart postcode matching when the search term looks like a postcode
 * @param {object} item - The job or marker object
 * @param {string} searchTerm - The search term
 * @returns {boolean} - True if the item matches the search term
 */
export const matchesSearchTerm = (item, searchTerm) => {
  if (!item || !searchTerm) return false;

  const term = searchTerm.toLowerCase().trim();

  // Check address fields
  const addressMatch =
    (item.streetAddress && item.streetAddress.toLowerCase().includes(term)) ||
    (item.formattedAddress && item.formattedAddress.toLowerCase().includes(term));

  // Check business name
  const businessNameMatch =
    item.businessName && item.businessName.toLowerCase().includes(term);

  // Smart postcode matching
  const postcodeMatch = matchItemPostcode(item, searchTerm);

  // Check other common fields
  const otherFieldsMatch = [
    item.skipId,
    item.size,
    item.wasteType,
    item.description,
    item.jobType,
    item.name,
    item.status,
    item.driverName
  ].some(field => field && field.toLowerCase().includes(term));

  return addressMatch || businessNameMatch || postcodeMatch || otherFieldsMatch;
};
